import type { CSSProperties } from "react";
import type { Database } from "@shared/database.types";

// Gli stati task non sono più un enum del DB: sono righe di task_statuses
// (personalizzabili per agenzia). La key è quindi una semplice stringa.
export type TaskStatusKey = string;
type TaskPriority = Database["public"]["Enums"]["task_priority"];

// ============================================================
// MAPPING CENTRALIZZATO (fonte di verità — non duplicare altrove)
//
// Stati: la fonte di verità runtime è la tabella task_statuses
// (via buildStatusMeta / useStatusMeta). TASK_STATUS_META resta
// come FALLBACK STATICO per le 6 key seed: serve dove non c'è
// React o mentre la pipeline è ancora in caricamento.
// ============================================================

export const TASK_STATUS_META: Record<
  TaskStatusKey,
  { label: string; column: number; color: string; bg: string }
> = {
  backlog: {
    label: "Backlog",
    column: 0,
    color: "text-muted-foreground",
    bg: "bg-muted-foreground/10",
  },
  urgente: {
    label: "Urgente",
    column: 1,
    color: "text-danger",
    bg: "bg-danger/15",
  },
  in_progress: {
    label: "In progress",
    column: 2,
    color: "text-primary",
    bg: "bg-primary/15",
  },
  review: {
    label: "Review",
    column: 3,
    color: "text-success",
    bg: "bg-success/15",
  },
  necessarie_revisioni: {
    label: "Da rivedere",
    column: 4,
    color: "text-warning",
    bg: "bg-warning/15",
  },
  done: {
    label: "Finito",
    column: 5,
    color: "text-success",
    bg: "bg-success/20",
  },
};

export const TASK_PRIORITY_META: Record<
  TaskPriority,
  { label: string; color: string; bg: string; borderClass: string }
> = {
  low: {
    label: "Bassa",
    color: "text-muted-foreground",
    bg: "bg-muted-foreground/10",
    borderClass: "priority-border-low",
  },
  medium: {
    label: "Media",
    color: "text-primary",
    bg: "bg-primary/15",
    borderClass: "priority-border-medium",
  },
  high: {
    label: "Alta",
    color: "text-warning",
    bg: "bg-warning/15",
    borderClass: "priority-border-high",
  },
  urgent: {
    label: "Urgente",
    color: "text-danger",
    bg: "bg-danger/15",
    borderClass: "priority-border-urgent",
  },
};

export const TASK_STATUS_ORDER: TaskStatusKey[] = [
  "backlog",
  "urgente",
  "in_progress",
  "review",
  "necessarie_revisioni",
  "done",
];

export const TASK_PRIORITY_ORDER: TaskPriority[] = [
  "low",
  "medium",
  "high",
  "urgent",
];

// Helper
export function statusLabel(s: TaskStatusKey): string {
  return TASK_STATUS_META[s]?.label ?? s;
}

export function priorityLabel(p: TaskPriority): string {
  return TASK_PRIORITY_META[p].label;
}

// ============================================================
// PIPELINE DINAMICA — meta derivata dalle righe task_statuses.
// Il colore è un DATO (hex dal DB), quindi va inline negli style
// (stesso pattern colore-dato di ListChrome/CardAccent).
// ============================================================

/** Sottoinsieme di task_statuses.Row sufficiente a derivare la grafica. */
export interface TaskStatusLike {
  key: string;
  label: string;
  color: string;
  sort_order: number;
  is_done: boolean;
  is_default: boolean;
  archived: boolean;
}

export interface StatusMeta {
  key: string;
  label: string;
  /** Hex dal DB */
  color: string;
  isDone: boolean;
  isDefault: boolean;
  archived: boolean;
  sortOrder: number;
  /** Badge: testo nel colore pieno + velo soffuso dello stesso colore */
  badgeStyle: CSSProperties;
  /** Pallino / filo colonna nel colore pieno */
  dotStyle: CSSProperties;
}

export interface StatusMetaMap {
  /** Stati attivi (non archiviati) in ordine di pipeline */
  list: StatusMeta[];
  /** Tutte le key note, comprese le archiviate */
  byKey: Map<string, StatusMeta>;
  /** Key dello stato di ingresso (fallback: primo attivo) */
  defaultKey: string;
  /** Key che contano come concluse (celebrazione, statistiche) */
  doneKeys: Set<string>;
  /** Lookup totale: mai undefined, degrada con label=key e oro brand */
  get(key: string): StatusMeta;
}

/** Oro brand: fallback per key sconosciute (task su stato appena rimosso, load). */
const FALLBACK_HEX = "#C9A24E";

export function statusBadgeStyle(hex: string): CSSProperties {
  return {
    color: hex,
    background: `color-mix(in srgb, ${hex} 14%, transparent)`,
  };
}

export function statusDotStyle(hex: string): CSSProperties {
  return { background: hex };
}

function toMeta(row: TaskStatusLike): StatusMeta {
  return {
    key: row.key,
    label: row.label,
    color: row.color,
    isDone: row.is_done,
    isDefault: row.is_default,
    archived: row.archived,
    sortOrder: row.sort_order,
    badgeStyle: statusBadgeStyle(row.color),
    dotStyle: statusDotStyle(row.color),
  };
}

/** Costruisce la meta della pipeline a partire dalle righe task_statuses. */
export function buildStatusMeta(rows: TaskStatusLike[]): StatusMetaMap {
  const sorted = [...rows].sort(
    (a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key),
  );
  const byKey = new Map<string, StatusMeta>();
  for (const r of sorted) byKey.set(r.key, toMeta(r));

  const list = sorted.filter((r) => !r.archived).map((r) => byKey.get(r.key)!);
  const defaultKey =
    list.find((m) => m.isDefault)?.key ?? list[0]?.key ?? "backlog";
  const doneKeys = new Set(
    sorted.filter((r) => r.is_done).map((r) => r.key),
  );

  return {
    list,
    byKey,
    defaultKey,
    doneKeys,
    get(key: string): StatusMeta {
      const found = byKey.get(key);
      if (found) return found;
      return {
        key,
        label: statusLabel(key),
        color: FALLBACK_HEX,
        isDone: false,
        isDefault: false,
        archived: false,
        sortOrder: Number.MAX_SAFE_INTEGER,
        badgeStyle: statusBadgeStyle(FALLBACK_HEX),
        dotStyle: statusDotStyle(FALLBACK_HEX),
      };
    },
  };
}

/**
 * Righe fallback = seed della migration 0032 (stessi hex e label).
 * Usate mentre la query task_statuses è in caricamento.
 */
export const FALLBACK_STATUS_ROWS: TaskStatusLike[] = [
  { key: "backlog", label: "Backlog", color: "#78716c", sort_order: 10, is_done: false, is_default: true, archived: false },
  { key: "urgente", label: "Urgente", color: "#ef4444", sort_order: 20, is_done: false, is_default: false, archived: false },
  { key: "in_progress", label: "In progress", color: "#3b82f6", sort_order: 30, is_done: false, is_default: false, archived: false },
  { key: "review", label: "Review", color: "#eab308", sort_order: 40, is_done: false, is_default: false, archived: false },
  { key: "necessarie_revisioni", label: "Da rivedere", color: "#f97316", sort_order: 50, is_done: false, is_default: false, archived: false },
  { key: "done", label: "Finito", color: "#22c55e", sort_order: 60, is_done: true, is_default: false, archived: false },
];
